import { Component, ChangeDetectionStrategy } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { FormComponentBase } from '../../../core/component/forms/form-component.base';
import { DataService } from '../../../core/service/base/data.service';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { DeleteFormCommandHandler } from '../../../core/service/command/delete-form-command-handler.service';
import { FormCommandHandlerResolver } from '../../../core/service/command/form-command-handler-resolver.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { ActivatedRoute } from '@angular/router';
import { standardCommand } from '../../../core/model/command';
import { CustomProperty } from '../../model/custom-property';
import { CustomPropertyService } from '../../service/data/custom-property.service';
import { ObjectComponentCategory } from '../../model/object-component-category';
import { ObjectComponentCategoryService } from '../../service/data/object-component-category.service';
import { ObjectComponentType } from '../../model/object-component-type';
import { ConnectedCustomProperty } from '../../model/connected-custom-property';
import { SelectionItem } from '../../../core/component/select/select.component';
import { FormControlEx } from '../../../core/component/forms/form-control';
import { Competence } from '../../../fault-notification/model/competence';

@Component({
    selector: 'app-object-component-category-form',
    templateUrl: './object-component-category-form.component.html',
    styleUrls: ['./object-component-category-form.component.scss'],
    providers: [DeleteFormCommandHandler, FormCommandHandlerResolver, { provide: DataService, useExisting: CustomPropertyService }],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ObjectComponentCategoryFormComponent extends FormComponentBase<ObjectComponentCategory> {
  constructor(
    private _fb: FormBuilder,
    private _fc: FormControlExFactory,
    activatedRoute: ActivatedRoute,
    commandDispatcher: CommandDispatcher,
    handlerResolver: FormCommandHandlerResolver,
    service: ObjectComponentCategoryService,
    private _customPropertyService: CustomPropertyService,
    messageService: MessageService,
    navigation: NavigationService
  ) {
    super(activatedRoute, service, messageService, navigation, commandDispatcher, handlerResolver);
    this.addMenuItemDefinition('CREATE_BTN', standardCommand.create);
  }

  get componentTypes(): FormArray {
    return this.form.controls['objectComponentTypes'] as FormArray;
  }

  protected createModel(): ObjectComponentCategory {
    return new ObjectComponentCategory();
  }

  protected getFormGroup(): FormGroup {
    return this._fb.group({
      name: this._fc.createWithOptions('CUSTOM_PROPERTY.CAPTION', this.dataContext.name, { length: 30, required: true }),
      objectComponentTypes: this._fb.array(this.dataContext.objectComponentTypes.map(type => this._createTypeForm(type))),
    });
  }

  private _createConnectedPropertyForm(property: ConnectedCustomProperty): FormGroup {
    return this._fb.group({
      id: this._fc.createHidden(property.id),
      property: this._fc.createMandatory('CUSTOM_PROPERTY.PROPERTY', this._getId(property.property)),
      mandatory: this._fc.createMandatory('CUSTOM_PROPERTY.MANDATORY', property.mandatory),
    });
  }

  private _createTypeForm(type: ObjectComponentType): FormGroup {
    return this._fb.group({
      id: this._fc.createHidden(type.id),
      name: this._fc.createWithOptions('COMPONENT.TYPE', type.name, { required: true, length: 30 }),
      lifeSpanYears: this._fc.create('COMPONENT.LIFESPAN_YEARS', type.lifeSpanYears),
      standardInBathroom: this._fc.create('COMPONENT.STANDARD_IN_BATHROOM', type.standardInBathroom),
      standardInBedroom: this._fc.create('COMPONENT.STANDARD_IN_BEDROOM', type.standardInBedroom),
      standardInDressingRoom: this._fc.create('COMPONENT.STANDARD_IN_DRESSINGROOM', type.standardInDressingRoom),
      standardInHallway: this._fc.create('COMPONENT.STANDARD_IN_HALLWAY', type.standardInHallway),
      standardInKitchen: this._fc.create('COMPONENT.STANDARD_IN_KITCHEN', type.standardInKitchen),
      standardInLivingRoom: this._fc.create('COMPONENT.STANDARD_IN_LIVINGROOM', type.standardInLivingRoom),
      standardInToilet: this._fc.create('COMPONENT.STANDARD_IN_TOILET', type.standardInToilet),
      standardInOther: this._fc.create('COMPONENT.STANDARD_IN_OTHER', type.standardInOther),
      standardInBalcony: this._fc.create('COMPONENT.STANDARD_IN_BALCONY', type.standardInBalcony),
      customProperties: this._fb.array(type.customProperties.map(item => this._createConnectedPropertyForm(item))),
    });
  }

  protected prepareSave(): ObjectComponentCategory {
    let model = super.prepareSave();
    //model.objectComponentTypes = this.
    //for (let index = 0; index < this.dataContext.objectComponentTypes.length; index++) {
    //  let type = this.dataContext.objectComponentTypes[index];
    //  let typeForm = (this.form.controls['componentTypes'] as FormGroup).controls[type.id] as FormGroup;
    //  type.name = typeForm.value.name;
    //  type.standardInBathroom = typeForm.value.standardInBathroom;
    //  type.lifeSpanYears = typeForm.value.lifeSpanYears;
    //  type.standardInBedroom = typeForm.value.standardInBedroom;
    //  type.standardInDressingRoom = typeForm.value.standardInDressingRoom;
    //  type.standardInHallway = typeForm.value.standardInHallway;
    //  type.standardInKitchen = typeForm.value.standardInKitchen;
    //  type.standardInOther = typeForm.value.standardInOther;
    //  type.standardInLivingRoom = typeForm.value.standardInLivingRoom;
    //  type.standardInToilet = typeForm.value.standardInToilet;
    //  for (let prpIndex = 0; prpIndex < type.customProperties.length; prpIndex++) {
    //    let prp = type.customProperties[prpIndex];
    //    let prpForm = (typeForm.controls['customProperties'] as FormGroup).controls[prp.id] as FormGroup;
    //    prp.mandatory = prpForm.value.mandatory;
    //    prp.property = this._customProperties.find(prop => prop.id === prpForm.value.property);
    //  }
    //}
    return model;
  }

  addProperty(index: number): void {
    const type = this.componentTypes.at(index) as FormGroup;
    const properties = type.controls['customProperties'] as FormArray;
    properties.push(this._createConnectedPropertyForm(new ConnectedCustomProperty()));
    this.form.markAsDirty();
  }

  deleteProperty(typeIndex: number, propertyIndex: number) {
    const typeGroup = this.componentTypes.at(typeIndex) as FormGroup;
    const customProperties = typeGroup.controls['customProperties'] as FormArray;
    customProperties.removeAt(propertyIndex);
    this.form.markAsDirty();
  }

  addType(): void {
    let type = new ObjectComponentType();
    type.componentCategory = this.dataContext.id;
    this.componentTypes.push(this._createTypeForm(type));
    this.form.markAsDirty();
  }

  deleteType(index: number): void {
    this.componentTypes.removeAt(index);
    this.form.markAsDirty();
  }

  private _customProperties: CustomProperty[];

  private _getId(competence: string | Competence | CustomProperty): string {
    if (typeof competence === 'string') return competence;
    if (!competence) return null;
    return competence.id;
  }

  protected init(data: ObjectComponentCategory) {
    this.AddSubscription(
      this._customPropertyService.getList().subscribe(list => {
        this._customProperties = list;
        for (let index = 0; index < this._customProperties.length; index++) {
          let prp = this._customProperties[index];
          this.propertySelection.push(new SelectionItem(prp.caption, prp.id));
        }
        super.init(data);
      })
    );
  }

  propertySelection: SelectionItem[] = [];
}
